import { Badge, Box, Flex, Heading, Strong, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonDialog } from 'components/common/dialogs';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDateInput } from 'components/common/form/date';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { endOfToday, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import useLatest from 'hooks/useLatestRef';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { UserRole } from 'lib_ts/enums/auth.enums';
import {
  LOCAL_PRETTY_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import {
  isCategoryReadOnly,
  ServiceCategory,
  shouldCategoryResetTraining,
} from 'lib_ts/enums/machine-service-logs.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Competition } from 'lib_ts/enums/team.enums';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IMachineServiceLog } from 'lib_ts/interfaces/i-machine-service-log';
import { clone, first } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { AdminTeamsService } from 'services/admin/teams.service';
import { MachineServiceLogsService } from 'services/machine-service-logs.service';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'MachineServiceLogs';

const CATEGORY_OPTIONS: IOptionExt[] = Object.values(ServiceCategory).map(
  (o) => ({
    label: o,
    value: o,
    disabled: isCategoryReadOnly(o),
    group: shouldCategoryResetTraining(o)
      ? 'Training: Reset'
      : 'Training: No Impact',
  })
);

interface IProps {
  machine: IMachine;
  model: Partial<IMachineServiceLog>;
  onClose: (reload: boolean) => void;
}

export const MachineServiceLogEditor = (props: IProps) => {
  const current = useAuthStore((s) => s.current);

  // before creating a category that would trigger a training reset, show the dialog to confirm
  const [dialogResetWarning, setDialogResetWarning] = useState<{
    key: number;
    categories: ServiceCategory[];
  }>();

  const [teamOptions, setTeamOptions] = useState<IOption[]>();

  // * pull teams from server directly to avoid relying on teams context
  // * reason: this can be used outside of the admin portal; e.g. in install wizard
  useEffect(() => {
    AdminTeamsService.getInstance()
      .getTeams(current.role === UserRole.admin)
      .then((result) =>
        setTeamOptions(
          result.map((m) => ({
            label: m.name,
            value: m._id,
            group: m.league ?? Competition.None,
          }))
        )
      );
  }, [current.role]);

  const [form, setForm] = useState<
    Partial<Omit<IMachineServiceLog, 'category'>>
  >(props.model);

  const [categories, setCategories] = useState(
    props.model.category ? [props.model.category] : []
  );

  const disabled = props.model.category
    ? isCategoryReadOnly(props.model.category)
    : false;

  const latestOnClose = useLatest(props.onClose);

  const handleSubmit = useCallback(
    async (force: boolean) => {
      const isValid = (category: ServiceCategory): boolean => {
        switch (category) {
          case ServiceCategory.BallReplacement: {
            if (!form.ball_type) {
              NotifyHelper.warning({
                message_md: 'Please provide a ball type and try again.',
              });
              return false;
            }

            if (!form.ball_count || form.ball_count <= 0) {
              NotifyHelper.warning({
                message_md: 'Please provide a ball count and try again.',
              });
              return false;
            }
            break;
          }

          case ServiceCategory.Installation: {
            if (!form.location) {
              NotifyHelper.warning({
                message_md: 'Please provide a new location and try again.',
              });
              return false;
            }

            if (!form.teamID) {
              NotifyHelper.warning({
                message_md: 'Please provide a team and try again.',
              });
              return false;
            }
            break;
          }

          default: {
            break;
          }
        }

        return true;
      };

      if (categories.length === 0) {
        NotifyHelper.warning({
          message_md: 'Please select at least one category and try again',
        });
        return;
      }

      if (!categories.every((c) => isValid(c))) {
        // toasts would show, don't proceed so the dialog doesn't close
        return;
      }

      const resetCategories = categories.filter((c) =>
        shouldCategoryResetTraining(c)
      );

      if (!force && resetCategories.length > 0) {
        // confirm before looping over the submission
        setDialogResetWarning({
          key: Date.now(),
          categories: resetCategories,
        });
        return;
      }

      // insert a new entry for each category
      for (const category of categories) {
        const payload: Partial<IMachineServiceLog> = {
          // * machineID, etc... from definition (that should never change)
          ...props.model,

          serviced: form.serviced,
          category: category,

          location:
            category === ServiceCategory.Installation ? form.location : null,

          teamID:
            category === ServiceCategory.Installation ? form.teamID : null,

          ball_type:
            category === ServiceCategory.BallReplacement
              ? form.ball_type
              : null,

          ball_count:
            category === ServiceCategory.BallReplacement
              ? form.ball_count
              : null,

          trainingReset: shouldCategoryResetTraining(category),

          notes: form.notes,
        };

        if (form._id) {
          await MachineServiceLogsService.getInstance().updateOne(
            form._id,
            payload
          );
        } else {
          await MachineServiceLogsService.getInstance().createOne(payload);
        }
      }

      latestOnClose.current(true);
    },
    [form, categories]
  );

  const isSingleCategory = !!form._id;

  const showBallCtrls = categories.includes(ServiceCategory.BallReplacement);
  const showRelocationCtrls = categories.includes(ServiceCategory.Installation);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.LG}
        title={
          <Flex align="center" gap="2">
            <Heading size={RADIX.HEADING.SIZE.MODAL_TITLE}>
              {form._id ? 'Modify Service Log' : 'Create Service Log'}
            </Heading>
            <Badge>{props.machine.machineID}</Badge>
          </Flex>
        }
        content={
          <CommonFormGrid columns={2}>
            <CommonDateInput
              id="serviced"
              label="Service Date"
              defaultValue={
                form.serviced ? parseISO(form.serviced) : new Date()
              }
              maxDate={endOfToday()}
              onChange={(value) => {
                if (!value) {
                  return;
                }

                setForm((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  const nextForm = clone(prev);
                  nextForm.serviced = value?.toISOString();
                  return nextForm;
                });
              }}
              disabled={disabled}
              showTime
              required
            />

            <CommonSearchInput
              id="categories"
              label={isSingleCategory ? 'Category' : 'Categories'}
              options={CATEGORY_OPTIONS}
              values={categories}
              onChange={(values) => {
                // for editing an existing record
                setForm((prev) => ({
                  ...prev,
                  category: first(values) as ServiceCategory,
                }));

                // for creating new records
                setCategories(values as ServiceCategory[]);
              }}
              disabled={disabled}
              // creating new records => a record will be spawned for each category
              multiple={!isSingleCategory}
              hint_md={
                categories.reduce(
                  (value, category) =>
                    value || shouldCategoryResetTraining(category),
                  false
                )
                  ? 'Training data will be reset'
                  : undefined
              }
            />

            {showBallCtrls && (
              <Box gridColumnStart="1">
                <CommonSelectInput
                  id="ball_type"
                  label="Ball Type"
                  value={form.ball_type ?? undefined}
                  options={Object.values(props.machine.ball_type_options).map(
                    (o) => ({
                      label: o,
                      value: o,
                    })
                  )}
                  onChange={(v) => {
                    setForm((prev) => {
                      if (!prev) {
                        return prev;
                      }

                      const nextForm = clone(prev);
                      nextForm.ball_type = v as BallType;
                      return nextForm;
                    });
                  }}
                  disabled={disabled}
                  skipSort
                />
              </Box>
            )}

            {showBallCtrls && (
              <CommonTextInput
                id="ball_count"
                label="Ball Count"
                value={form.ball_count?.toFixed(0)}
                onNumericChange={(v) => {
                  setForm((prev) => {
                    if (!prev) {
                      return prev;
                    }

                    const nextForm = clone(prev);
                    nextForm.ball_count = v;
                    return nextForm;
                  });
                }}
                disabled={disabled}
              />
            )}

            {showRelocationCtrls && teamOptions && (
              <CommonSelectInput
                id="team"
                label="Team"
                options={teamOptions}
                value={form.teamID ?? undefined}
                onChange={(v) => {
                  setForm((prev) => {
                    if (!prev) {
                      return prev;
                    }

                    const nextForm = clone(prev);
                    nextForm.teamID = v;
                    return nextForm;
                  });
                }}
                disabled={disabled}
                optional
              />
            )}

            {showRelocationCtrls && (
              <CommonTextInput
                id="location"
                label="New Location / Nickname"
                value={form.location ?? undefined}
                onChange={(v) => {
                  setForm((prev) => {
                    if (!prev) {
                      return prev;
                    }

                    const nextForm = clone(prev);
                    nextForm.location = v;
                    return nextForm;
                  });
                }}
                disabled={disabled}
                optional
              />
            )}

            <Box gridColumn="span 2">
              <CommonTextareaInput
                id="notes"
                label="Notes / Work Order #"
                value={form.notes}
                onChange={(v) => {
                  setForm((prev) => {
                    if (!prev) {
                      return prev;
                    }

                    const nextForm = clone(prev);
                    nextForm.notes = v;
                    return nextForm;
                  });
                }}
                disabled={disabled}
              />
            </Box>
          </CommonFormGrid>
        }
        buttons={[
          {
            label: 'Delete',
            color: RADIX.COLOR.DANGER,
            invisible: !form?._id,
            onClick: async () => {
              if (!form?._id) {
                return;
              }

              // delete the log record
              await MachineServiceLogsService.getInstance().deleteOne(form._id);

              props.onClose(true);
            },
          },
          {
            label: form._id ? 'Update' : 'Create',
            onClick: () => handleSubmit(false),
          },
        ]}
        onClose={() => props.onClose(false)}
      />

      {dialogResetWarning && (
        <CommonConfirmationDialog
          key={dialogResetWarning.key}
          identifier="ConfirmServiceLogResetCategories"
          title="Training Data Will be Reset"
          content={
            <Flex direction="column" gap="2">
              {form.serviced && (
                <Box>
                  <Text color={RADIX.COLOR.HINT}>
                    Service logs with the following categories will reset any
                    training data recorded before{' '}
                    <Strong>
                      {format(
                        parseISO(form.serviced),
                        LOCAL_PRETTY_DATETIME_FORMAT,
                        {
                          timeZone: LOCAL_TIMEZONE,
                        }
                      )}
                    </Strong>
                    :
                  </Text>

                  <ul>
                    {dialogResetWarning.categories.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                </Box>
              )}

              <Text color={RADIX.COLOR.HINT}>
                Deleting the service logs will restore that training data.
              </Text>
              <Text color={RADIX.COLOR.HINT}>
                Do you want to proceed and reset training data?
              </Text>
            </Flex>
          }
          action={{
            label: 'Proceed & Reset Training Data',
            color: RADIX.COLOR.DANGER,
            onClick: () => handleSubmit(true),
          }}
          cancel={{
            label: 'common.cancel',
          }}
        />
      )}
    </ErrorBoundary>
  );
};
